import { AnimatedHeading, AnimatedHeadingText } from './components/AnimatedHeading'
import { lazy, Suspense, useEffect, useRef, useState, type PropsWithChildren } from 'react'
import { BorderBeam } from 'border-beam'
import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { ArrowIcon, PhoneIcon } from './components/Icons'
import { FooterBrandLogo } from './components/FooterBrandLogo'
import { LiquidGlassFilter } from './components/LiquidGlassFilter'
import { HeroMark } from './components/HeroMark'
import { WebDesignPage } from './components/WebDesignPage'
import { StartProjectPage } from './components/StartProjectPage'
import { ProcessTimeline } from './components/ProcessTimeline'
import { CaseStudyView, ProductArtwork } from './components/CaseStudyView'
import { PayekoohCommunityLinks } from './components/PayekoohCommunityLinks'
import { caseStudies } from './content/caseStudies'
import { ScrollReveal } from './components/ScrollReveal'
import { SiteHeader, type Theme } from './components/SiteHeader'
import { MessengerIcon, type MessengerKind } from './components/MessengerIcons'
import { OfficeLocation, type OfficeMapLocation } from './components/OfficeLocation'
import { OfficeGallery } from './components/OfficeGallery'
import { copy, products, serviceCatalog, services, type Locale } from './content/site'

const consultationEmail = 'payegostaranhoshmand@gmail.com'
const PricingConceptsPage = lazy(() => import('./components/PricingConceptsPage'))
const PricingPage = lazy(() => import('./components/PricingPage'))
const messengerChannels: { kind: MessengerKind; href: string }[] = [
  { kind: 'eitaa', href: 'https://eitaa.com/payetech' },
  { kind: 'rubika', href: 'https://rubika.ir/payetech' },
  { kind: 'bale', href: 'https://ble.ir/payetech' },
  { kind: 'telegram', href: 'https://t.me/Payetech' },
  { kind: 'instagram', href: 'https://www.instagram.com/payegostaran/' },
  { kind: 'linkedin', href: 'https://www.linkedin.com/company/payegostaran/' },
]
const contactPhones = [
  { number: '09120930797', href: 'tel:+989120930797', label: 'projects' },
  { number: '09124641804', href: 'tel:+989124641804', label: 'technical' },
  { number: '02636631550', href: 'tel:+982636631550', label: 'office' },
] as const
const companyRegistrationPdf = '/documents/company-incorporation-notice.pdf'
const companyNewspaperPdf = '/documents/company-newspaper-notice.pdf'
const officeLocation: OfficeMapLocation = {
  name: {
    fa: 'پایه گستران هوشمند (پایه‌تک)',
    en: 'Paye Gostaran Hooshmand (Paye Tech)',
  },
  address: {
    fa: 'فردیس، حافظیه شمالی، کوچه تاج‌بخش، خیابان گلستان 64',
    en: 'Fardis, North Hafezieh, Tajbakhsh Alley, Golestan 64 Street',
  },
  mapsUrl: 'https://neshan.org/maps/places/76c2f18d3cc7de73b4f9e18e7893d00e#c35.739-50.994-20z-0p/35.739499995671274/50.993599992901636',
  embedUrl: 'https://neshan.org/maps/iframe/places/76c2f18d3cc7de73b4f9e18e7893d00e#c35.739-50.994-20z-0p/35.739499995671274/50.993599992901636',
}

function useLocale() {
  const [locale, setLocale] = useState<Locale>(() => {
    const saved = window.localStorage.getItem('pgh-locale')
    return saved === 'en' ? 'en' : 'fa'
  })

  useEffect(() => {
    const isFa = locale === 'fa'
    document.documentElement.lang = locale
    document.documentElement.dir = isFa ? 'rtl' : 'ltr'
    window.localStorage.setItem('pgh-locale', locale)
  }, [locale])

  return { locale, setLocale }
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    try { return window.localStorage.getItem('pgh-theme') === 'dark' ? 'dark' : 'light' }
    catch { return 'light' }
  })
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.documentElement.style.colorScheme = theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0b1020' : '#f2f5f3')
    try { window.localStorage.setItem('pgh-theme', theme) }
    catch { /* The selected theme still works for this visit. */ }
  }, [theme])
  return { theme, setTheme }
}

function ScrollRestoration() {
  const location = useLocation()
  useEffect(() => {
    const html = document.documentElement
    const previousBehavior = html.style.scrollBehavior
    html.style.scrollBehavior = 'auto'
    const target = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null
    if (target) target.scrollIntoView({ behavior: 'instant' })
    else window.scrollTo({ top: 0, behavior: 'instant' })
    const frame = requestAnimationFrame(() => { html.style.scrollBehavior = previousBehavior })
    return () => cancelAnimationFrame(frame)
  }, [location.pathname, location.hash])
  return null
}

function PageMetadata({ locale }: { locale: Locale }) {
  const location = useLocation()
  useEffect(() => {
    const webService = location.pathname === '/services/web-design'
    const projectStart = location.pathname === '/start-project'
    const pricingConcepts = location.pathname === '/pricing-concepts'
    const pricing = location.pathname === '/pricing'
    const slug = location.pathname.startsWith('/products/') ? location.pathname.split('/')[2] : null
    const selectedProduct = products.find((product) => product.slug === slug)
    const title = selectedProduct
      ? `${selectedProduct.name[locale]} | ${locale === 'fa' ? 'پایه‌تک' : 'Paye Tech'}`
      : projectStart
        ? (locale === 'fa' ? 'شروع همکاری و درخواست تماس | پایه‌تک' : 'Start a project | Paye Tech')
        : pricing
        ? (locale === 'fa' ? 'تعرفه خدمات و قیمت طراحی سایت | پایه‌گستران' : 'Service pricing and web design | Paye Gostaran')
        : pricingConcepts
        ? (locale === 'fa' ? 'نمونه‌های طراحی صفحهٔ تعرفه | پایه‌تک' : 'Pricing page concepts | Paye Tech')
        : webService
        ? (locale === 'fa' ? 'خدمات طراحی سایت | پایه‌تک' : 'Website design services | Paye Tech')
        : (locale === 'fa' ? 'پایه‌تک | طراحی سایت و اپلیکیشن' : 'Paye Tech | Websites, Apps & Custom Software')
    const summary = selectedProduct
      ? selectedProduct.description[locale]
      : projectStart
        ? (locale === 'fa' ? 'درخواست همکاری با پایه‌تک؛ ثبت شماره تماس و تکمیل اختیاری اطلاعات پروژه.' : 'Start a project with Paye Tech. Leave your number and optionally describe your project.')
        : pricing
        ? (locale === 'fa' ? 'تعرفه طراحی سایت، توسعه اپلیکیشن iOS و پشتیبانی پایه‌گستران. قیمت نهایی پس از بررسی نیاز پروژه اعلام می‌شود.' : 'Paye Gostaran pricing for web design, iOS app development and support. Final quotes follow a project review.')
        : pricingConcepts
        ? (locale === 'fa' ? 'سه نمونهٔ مفهومی و تعاملی برای طراحی صفحهٔ تعرفه. قیمت‌ها و امکانات فرضی‌اند.' : 'Three interactive pricing page concepts. Prices and scope are illustrative.')
        : webService
        ? (locale === 'fa' ? 'طراحی و توسعه انواع سایت شرکتی، فروشگاهی و اختصاصی در پایه‌تک.' : 'Website design and development at Paye Tech.')
        : (locale === 'fa' ? 'پایه‌تک؛ طراحی و برنامه‌نویسی وب‌سایت و اپلیکیشن، نرم‌افزار اختصاصی، اتوماسیون کسب‌وکار، هویت بصری و امور تبلیغات.' : 'PayeTech: web and mobile development, custom software, business automation, UX design, branding and digital creative services.')
    document.title = title
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    if (description) description.content = summary
    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')
    if (ogTitle) ogTitle.content = title
    const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]')
    if (ogDescription) ogDescription.content = summary
  }, [locale, location.pathname])
  return null
}

type ShellProps = PropsWithChildren<{
  locale: Locale
  onLocaleChange: (locale: Locale) => void
  theme: Theme
  onThemeChange: (theme: Theme) => void
}>

function Shell({ locale, onLocaleChange, theme, onThemeChange, children }: ShellProps) {
  return (
    <div className="site-shell">
      <LiquidGlassFilter />
      <a className="skip-link" href="#main">{locale === 'fa' ? 'رفتن به محتوای اصلی' : 'Skip to main content'}</a>
      <SiteHeader locale={locale} onLocaleChange={onLocaleChange} theme={theme} onThemeChange={onThemeChange} />
      <main id="main">{children}</main>
      <Footer locale={locale} theme={theme} />
    </div>
  )
}

function Hero({ locale }: { locale: Locale }) {
  const t = copy[locale].hero
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__inner">
        <div className="hero__copy">
          <h1 id="hero-title">
            <AnimatedHeadingText className="hero__headline-line">{t.titleA}</AnimatedHeadingText>
            <AnimatedHeadingText className="hero__headline-line"><em className="hero__accent">{t.titleB}</em> {t.titleC}</AnimatedHeadingText>
          </h1>
          <p className="hero__lead">{t.body}</p>
          <div className="hero__actions">
            <Link className="button button--primary" to="/start-project">{t.primary}<ArrowIcon /></Link>
            <a className="button button--secondary" href="#work">{t.secondary}</a>
          </div>
          <ul className="hero__assurances">
            {t.assurances.map((assurance) => <li key={assurance}>{assurance}</li>)}
          </ul>
        </div>
        <div className="hero__visual">
          <HeroMark locale={locale} />
        </div>
      </div>
      <a className="hero__scroll" href="#services" aria-label={locale === 'fa' ? 'رفتن به خدمات' : 'Scroll to services'}>SCROLL<span /></a>
    </section>
  )
}

function ServiceGlyph({ kind }: { kind: string }) {
  if (kind === 'mobile') return <svg viewBox="0 0 32 32" aria-hidden="true"><rect x="10" y="3" width="12" height="26" rx="3" /><path d="M14 6h4M15 25h2" /></svg>
  if (kind === 'systems') return <svg viewBox="0 0 32 32" aria-hidden="true"><rect x="3" y="6" width="26" height="20" rx="3" /><path d="m12 12-4 4 4 4m8-8 4 4-4 4m-2-10-4 20" /></svg>
  if (kind === 'web') return <svg viewBox="0 0 32 32" aria-hidden="true"><rect x="3" y="5" width="26" height="22" rx="3" /><path d="M3 11h26M7 8h1m3 0h1m-5 8h9v7H7zm13 0h5m-5 4h5" /></svg>
  if (kind === 'content') return <svg viewBox="0 0 32 32" aria-hidden="true"><rect x="5" y="4" width="22" height="24" rx="3" /><path d="M10 10h12m-12 5h12m-12 5h7" /></svg>
  return <svg viewBox="0 0 32 32" aria-hidden="true"><path d="m5 23 16-16 4 4-16 16H5v-4Zm13-13 4 4M6 6h6m-3-3v6m15 10h5m-2-2v5" /></svg>
}

function Services({ locale }: { locale: Locale }) {
  const t = copy[locale].services
  const [catalogId, setCatalogId] = useState<(typeof serviceCatalog)[number]['id']>('web')
  const [expanded, setExpanded] = useState(false)
  const catalog = serviceCatalog.find((category) => category.id === catalogId) ?? serviceCatalog[0]
  const shownItems = expanded ? catalog.items : catalog.items.slice(0, 4)
  const labels = locale === 'fa' ? {
    browse: 'بررسی گزینه‌ها',
    directory: 'حوزه‌های تخصصی',
    chooser: 'دسته‌بندی خدمات',
    available: 'خدمات قابل ارائه',
    viewAll: 'نمایش همه گزینه‌ها',
    viewLess: 'نمایش گزینه‌های کمتر',
    webGuide: 'راهنمای کامل طراحی سایت',
    consult: 'مشاوره و شروع پروژه',
    itemCount: 'گزینه',
  } : {
    browse: 'Explore options',
    directory: 'Our expertise',
    chooser: 'Service categories',
    available: 'Available services',
    viewAll: 'See all options',
    viewLess: 'Show fewer',
    webGuide: 'Website design guide',
    consult: 'Plan a project',
    itemCount: 'options',
  }

  function selectCatalog(id: (typeof serviceCatalog)[number]['id'], fromCard = false) {
    setCatalogId(id)
    setExpanded(false)
    if (fromCard) {
      document.getElementById('service-catalog')?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    }
  }

  return (
    <section id="services" className="services services--redesigned section-block" aria-labelledby="services-title">
      <div className="services__inner">
        <header className="services__heading">
          <div className="services__heading-copy">
            <p className="section-kicker">{t.kicker}</p>
            <h2 id="services-title">{t.titleA}<em>{t.titleAccent}</em>{t.titleB}</h2>
            <p className="services__intro">{t.intro}</p>
          </div>
          <Link className="services__all" to="/start-project">{labels.consult}<ArrowIcon /></Link>
        </header>

        <div className="services__eyebrow">
          <span>{labels.directory}</span>
          <span aria-hidden="true" className="services__eyebrow-rule" />
          <span dir="ltr">01 — 04</span>
        </div>
        <div className="service-grid">
          {services.map((service) => {
            const targetId = service.id === 'design' ? 'graphics' : service.id
            return (
              <article
                className={`service-card service-card--${service.id}${targetId === catalogId ? ' is-active' : ''}`}
                id={`service-${service.id}`}
                key={service.id}
              >
                <div className="service-card__top">
                  <span className="service-card__icon"><ServiceGlyph kind={service.id} /></span>
                  <span className="service-card__label">{service.label}</span>
                </div>
                <h3>{service.title[locale]}</h3>
                <p>{service.body[locale]}</p>
                <button
                  className="service-card__action"
                  type="button"
                  aria-label={`${labels.browse}: ${service.title[locale]}`}
                  onClick={() => selectCatalog(targetId, true)}
                >
                  <span>{labels.browse}</span><ArrowIcon />
                </button>
              </article>
            )
          })}
        </div>

        <section className="service-catalog" id="service-catalog" aria-labelledby="service-catalog-title">
          <div className="service-catalog__layout">
            <nav className="service-catalog__navigation" aria-label={labels.chooser}>
              <div className="service-catalog__nav-header">
                <span>{labels.chooser}</span>
                <span dir="ltr">05</span>
              </div>
              <div className="service-catalog__tabs">
                {serviceCatalog.map((category, index) => (
                  <button
                    className={category.id === catalogId ? 'is-active' : ''}
                    key={category.id}
                    type="button"
                    aria-pressed={category.id === catalogId}
                    aria-controls="service-catalog-items"
                    onClick={() => selectCatalog(category.id)}
                  >
                    <span className="service-catalog__tab-number" dir="ltr">{String(index + 1).padStart(2, '0')}</span>
                    <span className="service-catalog__tab-name">{category.title[locale]}</span>
                    <span className="service-catalog__tab-arrow" aria-hidden="true"><ArrowIcon /></span>
                  </button>
                ))}
              </div>
              <p className="service-catalog__nav-note">{t.catalogHint}</p>
            </nav>

            <div className="service-catalog__panel">
              <header className="service-catalog__heading">
                <div className="service-catalog__heading-copy">
                  <span className="service-catalog__overline">{labels.available} <span dir="ltr">/ {String(catalog.items.length).padStart(2, '0')} {labels.itemCount}</span></span>
                  <h3 id="service-catalog-title">{catalog.heading[locale]}</h3>
                  <p>{catalog.description[locale]}</p>
                </div>
                <Link className="service-catalog__consult" to="/start-project">{labels.consult}<ArrowIcon /></Link>
              </header>

              <div className="service-catalog__grid" id="service-catalog-items" key={catalog.id}>
                {shownItems.map((item, index) => (
                  <Link
                    className="service-type"
                    to="/start-project"
                    key={item.title.fa}
                    aria-label={`${t.discuss}: ${item.title[locale]}`}
                  >
                    <span className="service-type__number" dir="ltr">{String(index + 1).padStart(2, '0')}</span>
                    <span className="service-type__copy">
                      <strong>{item.title[locale]}</strong>
                      <small>{item.description[locale]}</small>
                    </span>
                    <span className="service-type__arrow"><ArrowIcon /></span>
                  </Link>
                ))}
              </div>

              <div className="service-catalog__footer">
                {catalog.items.length > 4 && (
                  <button
                    className="service-catalog__more"
                    type="button"
                    aria-controls="service-catalog-items"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                  >
                    {expanded ? labels.viewLess : labels.viewAll}
                    <span aria-hidden="true">{expanded ? '−' : '+'}</span>
                  </button>
                )}
                {catalog.id === 'web' && (
                  <Link to="/services/web-design" className="service-catalog__guide">{labels.webGuide}<ArrowIcon /></Link>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  )
}

function Work({ locale }: { locale: Locale }) {
  const t = copy[locale].work
  return (
    <section id="work" className="work section-block" aria-labelledby="work-title">
      <div className="section-heading section-heading--light section-grid">
        <p className="section-kicker">{t.kicker}</p>
        <AnimatedHeading as="h2" id="work-title">{t.title}</AnimatedHeading>
      </div>
      <div className="portfolio-grid">
        {products.map((product) => {
          const story = caseStudies[product.slug as keyof typeof caseStudies]
          return (
            <article className={`portfolio-card portfolio-card--${product.accent}`} key={product.slug}>
              <div className="portfolio-card__copy">
                {product.slug === 'payekooh-app' && (
                  <div className="portfolio-card__brand-row">
                    <img
                      src="/images/payekooh/payekooh-wordmark.svg"
                      width="130"
                      height="57"
                      alt={locale === 'fa' ? 'لوگوی اصلی پایه‌کوه' : 'Payekooh logo'}
                    />
                  </div>
                )}
                <p className="portfolio-card__eyebrow"><b dir="ltr">{product.index}</b>{story.context[locale]}</p>
                <h3><Link to={`/products/${product.slug}`}><AnimatedHeadingText>{product.name[locale]}</AnimatedHeadingText></Link></h3>
                <p className="portfolio-card__summary">{story.headline[locale]}</p>
                <ul className="portfolio-card__tags" aria-label={locale === 'fa' ? 'حوزه‌های محصول' : 'Product focus'}>
                  {story.tags[locale].map((tag) => <li key={tag}>{tag}</li>)}
                </ul>
                <div className="portfolio-card__actions">
                  <Link className="portfolio-card__action portfolio-card__action--main" to={`/products/${product.slug}`}>{t.more}<ArrowIcon /></Link>
                  {product.websiteUrl && <a className="portfolio-card__action portfolio-card__action--external" href={product.websiteUrl} target="_blank" rel="noopener noreferrer">{copy[locale].websiteAction}<ArrowIcon /></a>}
                  {product.marketplaceUrl && (
                    <a
                      className="portfolio-card__bazaar-action"
                      href={product.marketplaceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={copy[locale].marketplaceAction}
                      title={copy[locale].marketplaceAction}
                    >
                      <img src="/images/brands/cafebazaar.svg" alt="" width="28" height="28" />
                      <span>{locale === 'fa' ? 'کافه‌بازار' : 'Cafe Bazaar'}</span>
                    </a>
                  )}
                </div>
                {product.slug === 'payekooh-app' && (
                  <div className="portfolio-card__platforms" aria-label={locale === 'fa' ? 'پلتفرم‌های اپلیکیشن' : 'App platforms'}>
                    <span className="portfolio-card__platform portfolio-card__platform--android">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 11a7 7 0 0 1 14 0v7H5zM7 4l2 3m8-3-2 3M5 12v6m14-6v6M8 18v3m8-3v3" />
                        <circle cx="9" cy="11" r=".7" fill="currentColor" stroke="none" />
                        <circle cx="15" cy="11" r=".7" fill="currentColor" stroke="none" />
                      </svg>
                      <span>Android</span>
                    </span>
                    <span className="portfolio-card__platform portfolio-card__platform--soon" aria-label="iOS, Coming Soon">
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M16.2 2.4c-1.2.1-2.5.9-3.2 1.9-.7.9-1.2 2-1.1 3.1 1.3.1 2.5-.6 3.3-1.5.8-.9 1.2-2.1 1-3.5ZM19.3 16.5c-.4 1-.7 1.5-1.2 2.3-.8 1.2-1.8 2.7-3.2 2.7-1.2 0-1.6-.8-3.1-.8-1.6 0-2 .8-3.2.8-1.3.1-2.4-1.4-3.2-2.6-1.8-2.7-3.2-7.6-1.3-10.7 1-1.6 2.6-2.6 4.3-2.6 1.4 0 2.5.8 3.4.8.8 0 2.2-.9 3.9-.8 1.4.1 2.7.8 3.6 2-3.2 1.9-2.6 6.2.4 7.6Z" />
                      </svg>
                      <span>iOS</span>
                      <span className="portfolio-card__platform-note">Coming Soon</span>
                    </span>
                  </div>
                )}
                {product.slug === 'payekooh-app' && <PayekoohCommunityLinks locale={locale} />}
              </div>
              {product.slug === 'payekooh-app' && product.marketplaceUrl
                ? <div className="portfolio-card__visual-link portfolio-card__visual-link--showcase">
                    <ProductArtwork product={product} locale={locale} compact />
                  </div>
                : <Link className="portfolio-card__visual-link" to={`/products/${product.slug}`} aria-label={`${t.more}: ${product.name[locale]}`}>
                    <ProductArtwork product={product} locale={locale} compact />
                  </Link>}
            </article>
          )
        })}
      </div>
    </section>
  )
}

function About({ locale }: { locale: Locale }) {
  const t = copy[locale].about
  const officeImages = [
    '/images/about/office-overview.webp',
    '/images/about/office-meeting.webp',
  ] as const

  return (
    <section id="about" className="company-about section-block" aria-labelledby="about-title">
      <div className="company-about__container">
        <header className="company-about__intro">
          <div className="company-about__intro-heading">
            <p className="section-kicker">{t.kicker}</p>
            <AnimatedHeading as="h2" id="about-title">{t.title}</AnimatedHeading>
          </div>
          <div className="company-about__intro-copy">
            <p className="company-about__lead">{t.lead}</p>
            <p className="company-about__description">{t.body}</p>
          </div>
        </header>

        <OfficeGallery
          locale={locale}
          title={t.galleryTitle}
          viewFullImage={t.viewFullImage}
          images={t.gallery.map((image, index) => ({
            ...image,
            src: officeImages[index],
          }))}
        />

      </div>
    </section>
  )
}

function HomePage({ locale }: { locale: Locale }) {
  return <><Hero locale={locale} /><Services locale={locale} /><Work locale={locale} /><ProcessTimeline locale={locale} /><About locale={locale} /></>
}

function ProductPage({ locale }: { locale: Locale }) {
  const { slug } = useParams()
  const product = products.find((item) => item.slug === slug)
  if (!product) return <NotFound locale={locale} />
  return <CaseStudyView locale={locale} product={product} />
}

function PrivacyPage({ locale }: { locale: Locale }) {
  return (
    <article className="legal-page">
      <p className="section-kicker">{locale === 'fa' ? 'شفافیت' : 'Transparency'}</p>
      <AnimatedHeading as="h1">{locale === 'fa' ? 'حریم خصوصی' : 'Privacy'}</AnimatedHeading>
      <p>{locale === 'fa' ? 'شماره تماس و اطلاعاتی که در فرم درخواست همکاری وارد می‌کنید، فقط پس از ارسال موفق به سرویس ثبت درخواست منتقل می‌شوند تا برای پیگیری پروژه با شما تماس بگیریم. فرم تا زمان اتصال سرویس ذخیره‌سازی، هیچ درخواستی را به‌عنوان ثبت‌شده تأیید نمی‌کند.' : 'Contact details and any optional project information entered in our inquiry form are sent to the configured lead service only after a successful submission to arrange project follow-up. Until that service is connected, the site will not claim that your request was saved.'}</p>
      <p>{locale === 'fa' ? 'اگر روی پیوند موقعیت دفتر بزنید، صفحهٔ مکان در نشان باز می‌شود.' : 'The office location link opens its place page on Neshan.'}</p>
      <Link className="text-link" to="/"><ArrowIcon />{locale === 'fa' ? 'بازگشت به خانه' : 'Back home'}</Link>
    </article>
  )
}

function CompanyRegistrationPage({ locale }: { locale: Locale }) {
  const t = copy[locale].registration
  const facts = [
    { label: t.registrationNumber, value: t.registrationNumberValue },
    { label: t.nationalId, value: t.nationalIdValue },
    { label: t.registrationDate, value: t.registrationDateValue },
  ]
  const documents = [
    { id: 'incorporation', title: t.incorporationTitle, pdf: companyRegistrationPdf, preview: '/images/documents/incorporation-preview.webp', alt: t.previewAlt, width: 1819, height: 2573, downloadName: 'Paye-Gostaran-Incorporation-Notice.pdf' },
    { id: 'newspaper', title: t.newspaperTitle, pdf: companyNewspaperPdf, preview: '/images/documents/newspaper-preview.webp', alt: t.newspaperPreviewAlt, width: 1371, height: 1800, downloadName: 'Paye-Gostaran-Official-Newspaper-Notice.pdf' },
  ]

  return (
    <article className="company-record">
      <header className="company-record__hero">
        <div className="company-record__intro">
          <p className="section-kicker">{t.kicker}</p>
          <AnimatedHeading as="h1">{t.title}</AnimatedHeading>
          <p>{t.intro}</p>
          <div className="company-record__actions">
            <a className="button button--primary" href="#incorporation">{t.incorporationTitle}<ArrowIcon /></a>
            <a className="text-link" href="#newspaper">{t.newspaperTitle}<ArrowIcon /></a>
          </div>
        </div>
        <dl className="company-record__facts">
          {facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd dir="ltr">{fact.value}</dd></div>)}
        </dl>
      </header>
      <section className="company-record__document" aria-label={t.documentLabel}>
        <div className="company-record__documents">
          {documents.map((document) => (
            <article className="company-record__document-inner" id={document.id} key={document.id}>
              <div className="company-record__document-heading">
                <AnimatedHeading as="h2">{document.title}</AnimatedHeading>
                <div className="company-record__document-actions">
                  <a href={document.pdf} target="_blank" rel="noopener noreferrer" aria-label={`${document.title}: ${t.open} (${copy[locale].newTab})`}>{t.open}<ArrowIcon /></a>
                  <a href={document.pdf} download={document.downloadName}>{t.download}<ArrowIcon /></a>
                </div>
              </div>
              <a className="company-record__preview" href={document.pdf} target="_blank" rel="noopener noreferrer" aria-label={`${document.title}: ${t.open} (${copy[locale].newTab})`}>
                <img src={document.preview} alt={document.alt} width={document.width} height={document.height} loading="lazy" decoding="async" />
              </a>
            </article>
          ))}
        </div>
      </section>
    </article>
  )
}

function NotFound({ locale }: { locale: Locale }) {
  return <div className="not-found"><strong>404</strong><AnimatedHeading as="h1">{locale === 'fa' ? 'این صفحه پیدا نشد.' : 'This page was not found.'}</AnimatedHeading><Link className="button button--primary" to="/">{locale === 'fa' ? 'بازگشت به خانه' : 'Back home'}</Link></div>
}

const footerServiceLinks: Record<(typeof services)[number]['id'], string> = {
  design: '/#service-design',
  web: '/services/web-design',
  mobile: '/#service-mobile',
  systems: '/#service-systems',
}

const footerCopy = {
  fa: {
    description: 'پایه‌تک، تیم طراحی و توسعهٔ شرکت پایه گستران هوشمند؛ از هویت بصری و وب‌سایت تا نرم‌افزار و اپلیکیشن.',
    services: 'خدمات',
    products: 'محصولات',
    company: 'پایه‌تک',
    phone: 'تماس با مشاوره پروژه‌ها',
    email: 'ارسال ایمیل به پایه‌تک',
    office: 'مسیریابی دفتر در نشان',
    socials: 'شبکه‌های اجتماعی و پیام‌رسان‌ها',
    privacy: 'حریم خصوصی',
    legal: 'تمامی حقوق برای شرکت پایه گستران هوشمند محفوظ است.',
    trust: 'استعلام نماد اعتماد الکترونیکی پایه‌تک',
    trustCaption: 'بررسی اینماد',
    top: 'بازگشت به بالای صفحه',
  },
  en: {
    description: 'Paye Tech is the design and development team at Paye Gostaran Hooshmand, building brands, websites, software and mobile apps.',
    services: 'Services',
    products: 'Products',
    company: 'Paye Tech',
    phone: 'Call project consultations',
    email: 'Email Paye Tech',
    office: 'Directions to our office on Neshan',
    socials: 'Social networks and messengers',
    privacy: 'Privacy',
    legal: 'Paye Gostaran Hooshmand. All rights reserved.',
    trust: 'Verify the Paye Tech eNamad seal',
    trustCaption: 'Verify eNamad',
    top: 'Back to top',
  },
} as const

function FooterGlyph({ name }: { name: 'mail' | 'pin' | 'up' | 'shield' }) {
  if (name === 'mail') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 8 8 5.5L20 8" />
    </svg>
  )
  if (name === 'pin') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 0 1 14 0Z" /><circle cx="12" cy="10" r="2.5" />
    </svg>
  )
  if (name === 'shield') return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 2 8 3.5v6c0 5.2-3.2 8.6-8 10.5-4.8-1.9-8-5.3-8-10.5v-6L12 2Z" />
      <path d="m8.5 11.9 2.3 2.3 4.7-4.8" />
    </svg>
  )
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20V4m-6 6 6-6 6 6" />
    </svg>
  )
}

function Footer({ locale, theme }: { locale: Locale; theme: Theme }) {
  const t = copy[locale]
  const l = footerCopy[locale]
  const footerRef = useRef<HTMLElement>(null)
  const [sealImageLoaded, setSealImageLoaded] = useState(false)
  const [showTop, setShowTop] = useState(false)
  const [logoActive, setLogoActive] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const year = new Intl.DateTimeFormat(locale === 'fa' ? 'fa-IR-u-ca-persian' : 'en-US', { year: 'numeric' }).format(new Date())

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(preference.matches)
    update()
    preference.addEventListener('change', update)
    return () => preference.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const footer = footerRef.current
    if (!footer || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    footer.classList.add('paye-footer--motion')
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        footer.classList.add('paye-footer--visible')
        observer.disconnect()
      }
    }, { threshold: .06, rootMargin: '0px 0px -24px 0px' })
    observer.observe(footer)
    return () => {
      observer.disconnect()
      footer.classList.remove('paye-footer--motion', 'paye-footer--visible')
    }
  }, [])

  useEffect(() => {
    const update = () => setShowTop(window.scrollY > 540)
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])

  return (
    <>
      <footer id="contact" className="paye-footer" ref={footerRef} aria-label={locale === 'fa' ? 'ارتباط با پایه‌تک' : 'Contact Paye Tech'}>
        <div className="paye-footer__container">
          <div className="paye-footer__top">
            <div className="paye-footer__brand" data-footer-reveal>
              <BorderBeam className="paye-footer__logo-beam" size="md" colorVariant="ocean" strength={0.7} theme={theme} active={logoActive && !reduceMotion}>
                <Link
                  className="paye-footer__logo"
                  to="/"
                  aria-label={locale === 'fa' ? 'صفحه اصلی پایه‌تک' : 'Paye Tech home'}
                  onPointerEnter={() => setLogoActive(true)}
                  onPointerLeave={() => setLogoActive(false)}
                  onFocus={() => setLogoActive(true)}
                  onBlur={() => setLogoActive(false)}
                >
                  <FooterBrandLogo />
                </Link>
              </BorderBeam>
              <p className="paye-footer__description">{l.description}</p>
              <address className="paye-footer__contact">
                {contactPhones.map((phone) => (
                  <a href={phone.href} key={phone.number} aria-label={t.contact[phone.label] + ': ' + phone.number}>
                    <span className="paye-footer__contact-icon" aria-hidden="true"><PhoneIcon /></span>
                    <bdi className="paye-footer__phone-number" dir="ltr">{phone.number}</bdi>
                  </a>
                ))}
                <a href={'mailto:' + consultationEmail} aria-label={l.email}>
                  <span className="paye-footer__contact-icon" aria-hidden="true"><FooterGlyph name="mail" /></span>
                  <bdi dir="ltr">{consultationEmail}</bdi>
                </a>
                <a href={officeLocation.mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={l.office}>
                  <span className="paye-footer__contact-icon" aria-hidden="true"><FooterGlyph name="pin" /></span>
                  <span>{officeLocation.address[locale]}</span>
                </a>
              </address>
              <nav className="paye-footer__social" aria-label={l.socials}>
                {messengerChannels.map(({ kind, href }) => (
                  <a key={kind} href={href} target="_blank" rel="noopener noreferrer" title={t.contact.messengerNames[kind]} aria-label={t.contact.messengerNames[kind]}>
                    <MessengerIcon kind={kind} />
                  </a>
                ))}
              </nav>
            </div>
            <nav className="paye-footer__column" aria-label={l.services} data-footer-reveal>
              <ul>{services.map(service => (
                <li key={service.id}><Link to={footerServiceLinks[service.id]}>{service.title[locale]}</Link></li>
              ))}</ul>
            </nav>
            <nav className="paye-footer__column" aria-label={l.products} data-footer-reveal>
              <ul>
                {products.map(product => (
                  <li key={product.slug}><Link to={'/products/' + product.slug}>{product.name[locale]}</Link></li>
                ))}
                <li><Link to="/#work">{t.work.more}</Link></li>
              </ul>
            </nav>
            <nav className="paye-footer__column" aria-label={l.company} data-footer-reveal>
              <ul>
                <li><Link to="/#about">{t.nav.about}</Link></li>
                <li><Link to="/#process">{t.nav.process}</Link></li>
                <li><Link to="/#location">{t.nav.location}</Link></li>
                <li><Link to="/company/registration">{t.nav.registration}</Link></li>
                <li><Link to="/start-project">{t.nav.contact}</Link></li>
              </ul>
            </nav>
          </div>
          <div className="paye-footer__location" data-footer-reveal>
            <OfficeLocation locale={locale} location={officeLocation} showAddress={false} />
          </div>
          <div className="paye-footer__bottom" data-footer-reveal>
            <div className="paye-footer__verification">
              <a
                className={'paye-footer__trust' + (sealImageLoaded ? ' paye-footer__trust--loaded' : '')}
                referrerPolicy="origin"
                target="_blank"
                rel="noopener"
                href="https://trustseal.enamad.ir/?id=8087731&code=k0gv7Vkx8jxSwwXACS1VYHp7VqbX2ppm"
                aria-label={l.trust}
              >
                <img
                  referrerPolicy="origin"
                  src="https://trustseal.enamad.ir/logo.aspx?id=8087731&Code=k0gv7Vkx8jxSwwXACS1VYHp7VqbX2ppm"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="64"
                  height="64"
                  {...{ code: 'k0gv7Vkx8jxSwwXACS1VYHp7VqbX2ppm' }}
                  onLoad={() => setSealImageLoaded(true)}
                  onError={() => setSealImageLoaded(false)}
                />
                <span className="paye-footer__trust-fallback" aria-hidden="true"><FooterGlyph name="shield" /></span>
                <span>{l.trustCaption}</span>
              </a>
            </div>
            <div className="paye-footer__legal">
              <p><bdi>© {year}</bdi> {l.legal}</p>
              <nav aria-label={locale === 'fa' ? 'پیوندهای حقوقی' : 'Legal links'}>
                <Link to="/privacy">{l.privacy}</Link>
                <Link to="/company/registration">{t.nav.registration}</Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>
      <button
        className={'paye-footer__back-to-top' + (showTop ? ' is-visible' : '')}
        type="button"
        aria-label={l.top}
        title={l.top}
        onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })}
        tabIndex={showTop ? 0 : -1}
        aria-hidden={!showTop}
      >
        <FooterGlyph name="up" />
      </button>
    </>
  )
}

export function App() {
  const { locale, setLocale } = useLocale()
  const { theme, setTheme } = useTheme()
  return (
    <Shell locale={locale} onLocaleChange={setLocale} theme={theme} onThemeChange={setTheme}>
      <ScrollRestoration />
      <PageMetadata locale={locale} />
      <ScrollReveal />
      <Routes>
        <Route path="/" element={<HomePage locale={locale} />} />
        <Route path="/products/:slug" element={<ProductPage locale={locale} />} />
        <Route path="/services/web-design" element={<WebDesignPage locale={locale} />} />
        <Route path="/pricing-concepts" element={<Suspense fallback={<div className="legal-page">در حال آماده‌سازی نمونه‌ها…</div>}><PricingConceptsPage /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<div className="legal-page">در حال آماده‌سازی صفحهٔ قیمت‌گذاری…</div>}><PricingPage locale={locale} /></Suspense>} />
        <Route path="/start-project" element={<StartProjectPage locale={locale} />} />
        <Route path="/company/registration" element={<CompanyRegistrationPage locale={locale} />} />
        <Route path="/privacy" element={<PrivacyPage locale={locale} />} />
        <Route path="*" element={<NotFound locale={locale} />} />
      </Routes>
    </Shell>
  )
}
